// pages/company/Submissions.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Calendar,
  Filter,
  Search,
  TrendingUp,
  DollarSign,
  Building2,
  ChevronRight,
  PlusCircle,
  Sparkles
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { demoCompanyService, DemoSubmission } from '@/lib/demoCompanyService';
import { submissionService } from '@/lib/submissionService';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (value >= 1_000_000) {
    return `KES ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `KES ${(value / 1_000).toFixed(1)}K`;
  }
  return `KES ${value}`;
};

export default function CompanySubmissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<DemoSubmission[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [company, setCompany] = useState<any>(null);

  useEffect(() => {
    const demo = submissionService.isDemoMode();
    setIsDemoMode(demo);
    
    const currentCompany = demoCompanyService.getCurrentCompany();
    setCompany(currentCompany);
    
    loadSubmissions();
  }, []);

  const loadSubmissions = () => {
    const allSubmissions = demoCompanyService.getSubmissions();
    const currentCompany = demoCompanyService.getCurrentCompany();
    
    if (currentCompany) {
      // Filter submissions for current company
      const companySubmissions = allSubmissions.filter(s => s.companyId === currentCompany.id);
      setSubmissions(companySubmissions);
    } else {
      setSubmissions([]);
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'under_review': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accepted': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'under_review': return <Eye className="h-4 w-4" />;
      case 'accepted': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = sub.problemTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sub.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => s.status === 'pending').length,
    underReview: submissions.filter(s => s.status === 'under_review').length,
    accepted: submissions.filter(s => s.status === 'accepted').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
    totalImpact: submissions.reduce((sum, s) => sum + (s.estimatedImpactValue || 0), 0)
  };

  return (
    <div className="space-y-6 w-full">
      {/* Breadcrumb navigation */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Button variant="ghost" onClick={() => navigate('/company/dashboard')} className="h-auto p-0 text-muted-foreground hover:text-foreground">
          Dashboard
        </Button>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground font-medium">All Submissions</span>
      </div>

      {/* Header with title and action button */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Problem Submissions</h1>
          <p className="text-muted-foreground mt-1">
            Track and manage all your submitted industrial problems
          </p>
        </div>
        <div className="flex gap-2">
          {isDemoMode && (
            <Button 
              onClick={() => navigate('/company/submit?demo=true')}
              variant="outline"
              className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              <Sparkles className="h-5 w-5" />
              Generate Demo
            </Button>
          )}
          <Button 
            onClick={() => navigate('/company/submit')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 gap-2 h-11 px-6"
          >
            <PlusCircle className="h-5 w-5" />
            Submit New Problem
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="overflow-hidden border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Under Review</p>
            <p className="text-2xl font-bold text-blue-600">{stats.underReview}</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Accepted</p>
            <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Rejected</p>
            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Total Impact</p>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalImpact)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Card */}
      <Card className="overflow-hidden border-l-4 border-l-blue-500">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-11"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px] h-11">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <Card className="overflow-hidden border-l-4 border-l-blue-500">
          <CardContent className="py-16 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-xl font-semibold mb-2">No submissions found</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters to find what you\'re looking for'
                : 'Submit your first industrial problem to get started with Vett & Venture'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={() => navigate('/company/submit')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Submit a Problem
              </Button>
              {isDemoMode && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/company/submit?demo=true')}
                  className="border-purple-300 text-purple-700"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Demo
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSubmissions.map((submission) => (
            <Card 
              key={submission.id} 
              className="overflow-hidden border-l-4 border-l-blue-500 cursor-pointer hover:shadow-lg transition-all"
              onClick={() => navigate(`/company/submissions/${submission.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1 space-y-4">
                    {/* Status badges row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="outline" className={getStatusColor(submission.status) + ' px-3 py-1'}>
                        <span className="flex items-center gap-1">
                          {getStatusIcon(submission.status)}
                          {submission.status.replace('_', ' ')}
                        </span>
                      </Badge>
                      <Badge variant="outline" className={
                        submission.urgency === 'high' 
                          ? 'bg-red-100 text-red-700 border-red-200 px-3 py-1'
                          : submission.urgency === 'medium'
                          ? 'bg-yellow-100 text-yellow-700 border-yellow-200 px-3 py-1'
                          : 'bg-green-100 text-green-700 border-green-200 px-3 py-1'
                      }>
                        {submission.urgency} urgency
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 px-3 py-1">
                        <Building2 className="h-3 w-3 mr-1" />
                        {submission.companyName}
                      </Badge>
                    </div>

                    {/* Title and description */}
                    <div>
                      <h2 className="text-xl font-semibold mb-2">{submission.problemTitle}</h2>
                      <p className="text-muted-foreground line-clamp-2">
                        {submission.description}
                      </p>
                    </div>

                    {/* Review notes if available */}
                    {submission.reviewNotes && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-blue-700 mb-1">Analyst Feedback:</p>
                        <p className="text-sm text-blue-800">{submission.reviewNotes}</p>
                      </div>
                    )}

                    {/* Metadata row */}
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Submitted: {new Date(submission.submittedAt).toLocaleDateString()}</span>
                      </div>
                      {submission.reviewedAt && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>Reviewed: {new Date(submission.reviewedAt).toLocaleDateString()}</span>
                        </div>
                      )}
                      {submission.estimatedImpact && (
                        <div className="flex items-center gap-1 font-medium text-emerald-600">
                          <DollarSign className="h-4 w-4" />
                          <span>{submission.estimatedImpact}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* View details button */}
                  <div className="flex items-center">
                    <Button variant="outline" size="sm" className="gap-2">
                      <Eye className="h-4 w-4" />
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}